/* eslint-disable @typescript-eslint/no-explicit-any */

import { useAuth, useCart, useStore } from '@/containers'
import { SelectedIcon } from '@/styles/Icon'
import type { Province } from '@/types'
import { nonAccentVietnamese } from '@/utils/converter'
import { getSaleRegion } from '@/utils/endpoint'
import { Switch } from '@headlessui/react'
import { getCookie, setCookie } from 'cookies-next'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SearchInput } from '../SearchInput'
import PopupBody from './PopupBody'
import PopupFrame from './PopupFrame'
import PopupTitle from './PopupTitle'

const ModalChooseLocation = () => {
  const { setRefetch } = useAuth()
  const { guestInfo, setGuestInfo } = useCart()
  const {
    regions,

    setSaleRegion,
    isOpenPopupLocation,
    setIsOpenPopupLocation,
    areaType,
    setAreaType,
  } = useStore()

  const [localAreaType, setLocalAreaType] = useState<'old' | 'new'>(
    (getCookie('area-type') as 'old' | 'new') || areaType || 'old'
  )
  const provinceCode = getCookie('province') as string | undefined

  // Lưu giá trị gốc khi popup mở — dùng để revert nếu đóng mà không chọn
  const originalAreaTypeRef = useRef<'old' | 'new'>('old')

  // Danh sách tỉnh hiển thị trong modal (có thể khác `regions` nếu đổi areaType)
  const [displayRegions, setDisplayRegions] = useState<Province[]>(regions || [])
  const [isLoadingRegions, setIsLoadingRegions] = useState(false)

  /** Fetch danh sách tỉnh theo areaType */
  const fetchRegionsByType = useCallback(async (type: 'old' | 'new') => {
    setIsLoadingRegions(true)
    const data = await getSaleRegion(type)
    if (data) setDisplayRegions(data)
    setIsLoadingRegions(false)
  }, [])

  // Khi popup mở: lưu giá trị gốc, sync localAreaType từ cookie và load regions
  useEffect(() => {
    if (isOpenPopupLocation) {
      const cookieAreaType = getCookie('area-type') as 'old' | 'new' | undefined
      const resolvedType = cookieAreaType || areaType || 'old'
      originalAreaTypeRef.current = resolvedType
      setLocalAreaType(resolvedType)
      fetchRegionsByType(resolvedType)
    }
  }, [isOpenPopupLocation, areaType, fetchRegionsByType])

  /** Toggle switch — chỉ thay đổi state local, KHÔNG ghi cookie */
  const handleAreaTypeChange = async (checked: boolean) => {
    const newType = checked ? 'new' : 'old'
    setLocalAreaType(newType)
    // Fetch lại danh sách tỉnh theo areaType mới (chỉ preview, chưa lưu)
    await fetchRegionsByType(newType)
  }

  /** Đóng popup — revert areaType về giá trị gốc */
  const handleClose = () => {
    const original = originalAreaTypeRef.current
    setLocalAreaType(original)
    setAreaType(original)
    setIsOpenPopupLocation(false)
    setLocation('')
    localStorage?.setItem('area-popup-seen', 'true')
  }

  const [location, setLocation] = useState('')
  const t = useTranslations('Header')
  return (
    <PopupFrame
      open={isOpenPopupLocation}
      onClose={handleClose}
      type='choose_location'
      classNameDialog='!z-[9999]'
      className='z-[9999] min-h-[480px] max-w-[520px] bg-white-400'
    >
      <PopupTitle className='px-4 pt-3' type={'choose_location'}>
        {
          <div className='flex flex-wrap'>
            <p className='w-full pr-8 text-Headline font-medium'>
              {t('selectLocation')}
            </p>

            <SearchInput
              placeholder={t('selectLocationPld')}
              className='mb-3 mt-2'
              value={location}
              onChange={e => setLocation(e.target?.value)}
            />
          </div>
        }
      </PopupTitle>
      <PopupBody>
        <div className='listLocation border-grey-100 mt-[18px] flex h-full max-h-[300px] flex-wrap justify-between gap-y-[12px] overflow-scroll  px-[24px] scrollbar-hide'>
          {isLoadingRegions ? (
            <div className='flex w-full items-center justify-center py-8'>
              <span className='text-sm text-dark-400'>Đang tải...</span>
            </div>
          ) : (
            displayRegions
              ?.sort((a, b) => (a.order > b.order ? 1 : -1))
              .filter(store =>
                nonAccentVietnamese(store.name).includes(
                  nonAccentVietnamese(location)
                )
              )
              .map((item, index) => (
                <button
                  key={item.code || index}
                  className={`${
                    provinceCode === item.code && ' bg-blue-100'
                  } border-grey-100 h-10 w-[48%] border-b-[1px] text-Headline text-dark-400`}
                  onClick={async () => {
                    const areaCode = item.parentCode

                    setRefetch(true)
                    setIsOpenPopupLocation(false)

                    await setSaleRegion(areaCode)
                    await localStorage?.setItem('area-popup-seen', 'true')
                    localStorage.setItem('Area-Code', areaCode)
                    setCookie('Area-Code', areaCode, {
                      maxAge: 2147483647,
                    })

                    // Lưu province = tỉnh đã chọn (item.code), KHÔNG phải default
                    localStorage.setItem('province', item.code)
                    setCookie('province', item.code, {
                      maxAge: 2147483647,
                    })

                    // Lưu area-type theo giá trị hiện tại (có thể là 'new' nếu user đã toggle)
                    localStorage.setItem('area-type', localAreaType)
                    setCookie('area-type', localAreaType, { maxAge: 2147483647 })

                    setRefetch(false)
                    setAreaType(localAreaType)
                    setGuestInfo({
                      ...guestInfo,
                      provinceCode: item.code,
                      districtCode: '',
                      wardCode: '',
                      address: '',
                      saleRegionCode: areaCode,
                      areaType: localAreaType,
                    })
                    if (window.location.search.includes('shippingAddress')) {
                      const url = new URL(window.location.href)
                      url.searchParams.delete('shippingAddress')
                      url.searchParams.delete('pis')
                      window.history.pushState({}, '', url.toString())
                    }
                    window.location.reload()
                  }}
                >
                  <div className='flex items-center'>
                    <span className='flex-auto flex w-9 justify-center text-center'>
                      {provinceCode === item.code && <SelectedIcon />}
                    </span>
                    <span
                      className={
                        provinceCode === item.code
                          ? 'flex-auto font-semibold'
                          : 'flex-auto'
                      }
                    >
                      {item.name}
                    </span>
                  </div>
                </button>
              ))
          )}
        </div>

        {/* Switch đổi đơn vị hành chính */}
        <div className='mt-4 flex items-center gap-x-3 border-t border-dark-100 px-[24px] pb-3 pt-4'>
          <Switch
            checked={localAreaType === 'new'}
            onChange={handleAreaTypeChange}
            className={`${
              localAreaType === 'new' ? 'bg-primary-base' : 'bg-[#BFBFBF]'
            } relative inline-flex w-11 cursor-pointer items-center rounded-full py-[3px] transition`}
          >
            <span className='sr-only'>Đơn vị hành chính mới</span>
            <span
              className={`${
                localAreaType === 'new'
                  ? 'translate-x-[22px]'
                  : 'translate-x-[3px]'
              } inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow transition`}
            />
          </Switch>
          <div className='flex flex-col'>
            <span className='cursor-pointer text-sm font-medium text-dark-400'>
              Sử dụng đơn vị hành chính mới
            </span>
          </div>
        </div>
      </PopupBody>
    </PopupFrame>
  )
}

export default ModalChooseLocation
